import React from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { withoutEmojis } from "../../../utils/emoji";


const FormField = ({
  label,
  type = "text", // text | unit | unitSelect | bp | height | autocomplete | textarea
  value,
  onChange,
  unit,
  unitOptions = [],
  onUnitChange,
  systolic,
  diastolic,
  onChangeSys,
  onChangeDia,
  heightUnit,
  onHeightUnitChange,
  feet,
  inches,
  onFeetChange,
  onInchesChange,
  options = [],
  autoValue = [],
  onAutoChange,
  placeholder,
}) => {
  // Emojis are not accepted in any field.
  const handleChange = withoutEmojis(onChange);
  const handleChangeSys = withoutEmojis(onChangeSys);
  const handleChangeDia = withoutEmojis(onChangeDia);
  const handleFeetChange = withoutEmojis(onFeetChange);
  const handleInchesChange = withoutEmojis(onInchesChange);

  const containerStyle = {
    display: "flex",
    alignItems: "center",
    height: "48px",
    borderRadius: "8px",
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    padding: "0 12px",
    fontFamily: "Figtree",
  };

  const inputStyle = {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "14px",
    fontFamily: "Figtree",
    background: "transparent",
    color: "var(--app-text)",
  };

  const divider = (
    <Box
      sx={{
        width: "1px",
        height: "100%",
        backgroundColor: "var(--app-border)",
        mx: 1,
      }}
    />
  );

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "var(--app-text-muted)",
            mb: "6px",
            fontFamily: "Figtree",
          }}
        >
          {label}
        </Typography>
      )}

      {/* TEXT */}
      {type === "text" && (
        <Box sx={containerStyle}>
          <input
            value={value || ""}
            onChange={handleChange}
            placeholder={placeholder}
            style={inputStyle}
          />
        </Box>
      )}

      {/* UNIT (Temperature, SPO2, RR) */}
      {type === "unit" && (
        <Box sx={containerStyle}>
          <input
            value={value || ""}
            onChange={handleChange}
            placeholder={placeholder}
            style={inputStyle}
          />
          {divider}
          <Typography sx={{ fontWeight: 500 , fontSize: "15px", color: "var(--app-text)" }}>{unit}</Typography>
        </Box>
      )}

      {/* UNIT SELECT (Weight, Sugar) */}
      {type === "unitSelect" && (
        <Box sx={containerStyle}>
          <input
            value={value || ""}
            onChange={handleChange}
            placeholder={placeholder}
            style={inputStyle}
          />
          {divider}
          <Select
            value={unit}
            onChange={(e) => onUnitChange(e.target.value)}
            variant="standard"
            disableUnderline
            IconComponent={KeyboardArrowDownIcon}
            sx={{ fontWeight: 500 , fontSize: "15px", color: "var(--app-text)"  }}
          >
            {unitOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      {/* BLOOD PRESSURE */}
      {type === "bp" && (
        <Box sx={containerStyle}>
          <input
            value={systolic || ""}
            onChange={handleChangeSys}
            placeholder="Systolic"
            style={inputStyle}
          />
          {divider}
          <input
            value={diastolic || ""}
            onChange={handleChangeDia}
            placeholder="Diastolic"
            style={inputStyle}
          />
          {divider}
          <Typography sx={{ fontWeight: 500 , fontSize: "15px", color: "var(--app-text)"  }}>{unit}</Typography>
        </Box>
      )}

      {/* HEIGHT */}
      {type === "height" && (
        <Box sx={containerStyle}>
          {heightUnit === "cm" ? (
            <>
              <input
                value={value || ""}
                onChange={handleChange}
                placeholder="Enter Height"
                style={inputStyle}
              />
              {divider}
              <Select
                value={heightUnit}
                onChange={(e) => onHeightUnitChange(e.target.value)}
                variant="standard"
                disableUnderline
                IconComponent={KeyboardArrowDownIcon}
                sx={{ fontWeight: 500 , fontSize: "15px", color: "var(--app-text)"  }}
              >
                <MenuItem value="cm">cm</MenuItem>
                <MenuItem value="ft">ft & inches</MenuItem>
              </Select>
            </>
          ) : (
            <>
              <input
                value={feet || ""}
                onChange={handleFeetChange}
                placeholder="Feet"
                style={inputStyle}
              />
              {divider}
              <input
                value={inches || ""}
                onChange={handleInchesChange}
                placeholder="Inches"
                style={inputStyle}
              />
              {divider}
              <Select
                value={heightUnit}
                onChange={(e) => onHeightUnitChange(e.target.value)}
                variant="standard"
                disableUnderline
                IconComponent={KeyboardArrowDownIcon}
                sx={{ fontWeight: 500 , fontSize: "15px", color: "var(--app-text)"  }}
                
              >
                <MenuItem value="cm">cm</MenuItem>
                <MenuItem value="ft">ft & inches</MenuItem>
              </Select>
            </>
          )}
        </Box>
      )}

      {/* AUTOCOMPLETE (Symptoms) */}
      {type === "autocomplete" && (
        <Autocomplete
          multiple
          freeSolo
          options={options}
          value={autoValue}
          onChange={onAutoChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.label || option} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              size="small"
              inputProps={{
                ...params.inputProps,
                // freeSolo: clean what is typed so a chip can never carry one
                onChange: withoutEmojis(params.inputProps.onChange),
              }}
            />
          )}
        />
      )}

      {/* TEXTAREA */}
      {type === "textarea" && (
        <TextField
          fullWidth
          multiline
          rows={4}
          value={value || ""}
          onChange={handleChange}
          placeholder={placeholder}
        />
      )}
    </Box>
  );
};

export default FormField;